/**
 * Compares two extractions of the same page so a reviewer can tell whether a correction helped.
 *
 * Without this, re-prompting is a guess: the table simply redraws and the only way to know whether
 * the new result is better is to re-read every row. The whole point of a correction loop is being
 * able to see what moved.
 *
 * Pure, so the rules are testable without rendering anything.
 */

import type { ExtractedGame } from '../../../../../types/schedule-ingestion';

export interface GameDiff {
  added: ExtractedGame[];
  removed: ExtractedGame[];
  changed: Array<{ before: ExtractedGame; after: ExtractedGame }>;
  unchanged: number;
}

/**
 * Identity for diffing: the teams playing, plus the date.
 *
 * Deliberately NOT sortOrder — a re-extraction renumbers every row, so keying on it would report
 * an entire schedule as replaced whenever one game was inserted near the top. Deliberately not
 * time or field either, since correcting those is the common case and they should read as a change
 * to a game rather than as one game vanishing and another appearing.
 */
function gameKey(game: ExtractedGame): string {
  return [game.date, game.homeTeam, game.awayTeam]
    .map((part) => (part ?? '').trim().toLowerCase())
    .join('|');
}

/** The fields a reviewer would call a change to an existing game. */
function isChanged(a: ExtractedGame, b: ExtractedGame): boolean {
  return a.time?.trim() !== b.time?.trim() || a.field?.trim() !== b.field?.trim();
}

export function diffGames(
  before: ExtractedGame[],
  after: ExtractedGame[]
): GameDiff {
  const beforeByKey = new Map(before.map((g) => [gameKey(g), g]));
  const afterByKey = new Map(after.map((g) => [gameKey(g), g]));

  const added: ExtractedGame[] = [];
  const changed: GameDiff['changed'] = [];
  let unchanged = 0;

  for (const [key, game] of afterByKey) {
    const previous = beforeByKey.get(key);
    if (!previous) {
      added.push(game);
    } else if (isChanged(previous, game)) {
      changed.push({ before: previous, after: game });
    } else {
      unchanged += 1;
    }
  }

  const removed = [...beforeByKey]
    .filter(([key]) => !afterByKey.has(key))
    .map(([, game]) => game);

  return { added, removed, changed, unchanged };
}

/** True when nothing moved — worth saying explicitly, since it means the correction had no effect. */
export function isNoOpDiff(diff: GameDiff): boolean {
  return (
    diff.added.length === 0 &&
    diff.removed.length === 0 &&
    diff.changed.length === 0
  );
}
